import { FunnelId } from 'constants/funnel';
import AuthorByLine from 'components/Shared/AuthorByLine';
import ConversionBar from 'components/Shared/ConversionBar';
import GetInTouch from 'components/Shared/GetInTouch';
import GuideOrTreatmentStructuredData from 'components/Shared/GuideOrTreatmentStructuredData';
import ReviewerSideLine from 'components/Shared/ReviewerSideLine';
import Reviews from 'components/Shared/Reviews/Reviews';
import Breadcrumbs from 'components/ui/Breadcrumbs';
import BlockLoader from 'components/ui/ContentBlocks/BlockLoader';
import NavigationBar from 'components/ui/NavigationBar';
import { useTranslation } from 'hooks/useTranslation';
import { useRouter } from 'next/router';
import { createRef, useEffect, useState } from 'react';
import { formatCurrency } from 'utils/currency';
import TableOfContents, { getTitles } from 'components/Shared/TableOfContents';
import clsx from 'clsx';
import BeforeAfterPhotosSection from './BeforeAfterPhotosSection';
import Costs from './Costs';
import Details from './Details';
import Overview from './Overview';
import styles from './TreatmentPage.module.scss';
import type { TreatmentPage as TreatmentPageType } from 'types/contentful/generated/graphql';
import type { ReviewItem } from 'types/Review';
import type { APIBeforeAfterPhoto } from 'types/BeforeAfterPhotos';
import type { PageAuthors } from 'types/Author';
import type { NavBarItems } from 'components/ui/NavigationBar';

type Props = {
  entry: TreatmentPageType;
  resultState?: ReviewItem[];
  reviewStats?: Record<string, number>;
  sidebar: JSX.Element;
  beforeAfterPhotos: APIBeforeAfterPhoto[];
  baPhotosTotalCount: number | string;
  pageAuthors: PageAuthors;
  hasClinicsAndDoctorsData?: boolean;
};

const TreatmentPage = ({
  entry,
  resultState,
  reviewStats,
  sidebar,
  beforeAfterPhotos,
  baPhotosTotalCount,
  pageAuthors,
  hasClinicsAndDoctorsData,
}: Props): JSX.Element => {
  const router = useRouter();
  const { locale, t } = useTranslation();
  const funnelId = entry.funnel ? entry.funnel.sys.id : FunnelId.CONTACT_FORM;

  const [activeItem, setActiveItem] = useState<number>(-1);

  const hasReviews = !!reviewStats?.total || !!resultState?.length;

  const { costsCollection, detailsCollection, overviewCollection } =
    entry || {};

  const isClinicList = !entry.hasDoctors;
  const specialityFields = entry.speciality;

  const titles = getTitles({
    items: [
      ...(overviewCollection?.items || []),
      ...(detailsCollection?.items || []),
      ...(costsCollection?.items || []),
    ],
  });

  const isTableOfContentsShown = titles && entry.isTableOfContentsEnabled;

  const isSectionEmpty = (
    blocks: NonNullable<
      | TreatmentPageType['detailsCollection']
      | TreatmentPageType['overviewCollection']
      | TreatmentPageType['costsCollection']
      | TreatmentPageType['reviewsCollection']
    >,
  ): boolean => {
    return blocks.items.every((block) => {
      if (block && 'disableLocal' in block) {
        return !!block.disableLocal;
      }

      return false;
    });
  };

  const createInitialNavBarItems = (): NavBarItems => {
    const initialNavBarItems: NavBarItems = {
      byId: {},
      allIds: [],
    };

    for (const key of Object.keys(entry)) {
      // eslint-disable-next-line
      // @ts-ignore
      if (!entry[key as keyof typeof entry]?.items?.length) {
        continue;
      }

      switch (key) {
        case 'detailsCollection':
        case 'overviewCollection':
        case 'costsCollection':
        case 'reviewsCollection': {
          const blocks = entry[key];

          // HOTFIX: need to do that in order to fit the translations key
          const shortKey = key.replace('Collection', '');

          initialNavBarItems.byId[key] = {
            anchorIdOrUrl: `#${shortKey}`,
            text: t(`treatmentPage.${shortKey}`),
            ref: createRef(),
            hideOnMobile: key === 'detailsCollection',
            isEmpty: !blocks || isSectionEmpty(blocks),
          };

          initialNavBarItems.allIds.push(key);
        }
      }
    }

    if (hasReviews && !('reviews' in initialNavBarItems.byId)) {
      const reviewsKey = 'reviews';
      initialNavBarItems.byId.reviews = {
        anchorIdOrUrl: `#${reviewsKey}`,
        text: t(`treatmentPage.${reviewsKey}`),
        ref: createRef(),
        hideOnMobile: false,
      };

      initialNavBarItems.allIds.push(reviewsKey);
    }

    if (hasClinicsAndDoctorsData) {
      const { hasDoctors } = entry || {};
      const clinicListUrl = `/${locale}/${specialityFields?.slug}/${entry.slug}/clinics`;

      if (hasDoctors === null || typeof hasDoctors === 'undefined') {
        initialNavBarItems.byId.clinics = {
          anchorIdOrUrl: clinicListUrl,
          text: t('treatmentPage.clinics'),
        };

        initialNavBarItems.allIds.push('clinics');
      }

      if (hasDoctors) {
        initialNavBarItems.byId.doctors = {
          anchorIdOrUrl: clinicListUrl,
          text: isClinicList
            ? t('clinicList.clinics')
            : t('doctorsList.doctors'),
        };

        initialNavBarItems.allIds.push('doctors');
      }
    }

    return initialNavBarItems;
  };

  const [navBarItems, setNavBarItems] = useState<NavBarItems>(
    createInitialNavBarItems(),
  );

  useEffect(() => {
    for (const key of Object.keys(navBarItems.byId)) {
      const navBarItem = navBarItems.byId[key];
      if (navBarItem) {
        // HOTFIX: need to do that in order to fit the translations key
        const shortKey = key.replace('Collection', '');

        navBarItem.text = t(`treatmentPage.${shortKey}`);
        if (['clinics', 'doctors'].includes(key)) {
          navBarItem.anchorIdOrUrl = `${router.asPath.match(
            /[^(?|#)]*/i,
          )?.[0]}/clinics`;
        }
      }
    }

    setNavBarItems({ ...navBarItems });
  }, [router.query.locale]);

  useEffect(() => {
    let targetInView: HTMLElement | null = null;
    let io: IntersectionObserver | null = null;

    const getNavBarItemIndex = (sectionId: string): number => {
      return navBarItems.allIds.findIndex((item) => item === sectionId);
    };

    const observeSections = (): void => {
      for (const id of navBarItems.allIds) {
        const navBarItemElement = navBarItems.byId[id].ref?.current;
        if (!navBarItemElement) {
          break;
        }

        io?.observe(navBarItemElement);
      }
    };

    const checkTargetsInView = (entries: IntersectionObserverEntry[]): void => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          if (entry.target === targetInView) {
            // "targetInView" is leaving the view, set
            // it to null.
            targetInView = null;
          }

          break;
        }

        targetInView = entry.target as HTMLElement;
        const activeItemIndex = getNavBarItemIndex(targetInView.id);

        if (activeItemIndex != null) {
          setActiveItem(activeItemIndex);
        }
      }

      if (!targetInView) {
        // If none of the observed targets is currently in view,
        // set activeItem to -1 to disable navbar highlight
        setActiveItem(-1);
      }
    };

    const createObserver = (
      callback: IntersectionObserverCallback,
    ): IntersectionObserver => {
      const options: IntersectionObserverInit = {
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0,
      };

      return new IntersectionObserver(callback, options);
    };

    io = createObserver(checkTargetsInView);
    observeSections();

    return () => {
      io?.disconnect();
    };
  }, []);

  const breadcrumbs = [
    {
      link: specialityFields?.slug as string,
      title:
        specialityFields?.specialityName || (specialityFields?.title as string),
    },
    {
      link: `${specialityFields?.slug}/${entry.slug}`,
      title: entry.treatmentName || (entry.title as string),
    },
  ];

  return (
    <>
      <GuideOrTreatmentStructuredData entry={entry} crumbs={breadcrumbs} />
      <Breadcrumbs
        containerClassNames={styles.breadcrumbsContainerMobile}
        crumbs={breadcrumbs}
      />
      <div className={styles.hero}>
        <Breadcrumbs
          containerClassNames={styles.breadcrumbsContainerDesktop}
          crumbs={breadcrumbs}
        />
        <div className={clsx(styles.container, styles.titleContainer)}>
          <span className={styles.titleLabel}>
            {t('treatmentPage.treatmentGuide')}
          </span>
          <h1 className={styles.title}>{entry.title}</h1>
          {pageAuthors && (
            <AuthorByLine
              authorEntry={pageAuthors}
              lastUpdated={entry.sys.publishedAt}
            />
          )}
        </div>
      </div>
      <NavigationBar items={navBarItems} activeItemIndex={activeItem} />

      <div className={styles.container}>
        <div className={styles.treatmentPage}>
          <div className={styles.overview}>
            {overviewCollection?.items && (
              <Overview
                entry={entry}
                ref={navBarItems?.byId?.overview?.ref || null}
              />
            )}
            {beforeAfterPhotos.length > 0 && (
              <BeforeAfterPhotosSection
                beforeAfterPhotos={beforeAfterPhotos}
                slug={entry.slug as string}
                baPhotosTotalCount={baPhotosTotalCount}
              />
            )}
            {costsCollection && !isSectionEmpty(costsCollection) && (
              <Costs
                entry={entry}
                ref={navBarItems?.byId?.costs?.ref || null}
              />
            )}
            {detailsCollection && !isSectionEmpty(detailsCollection) && (
              <Details
                entry={entry}
                ref={navBarItems?.byId?.details?.ref || null}
              />
            )}
            {hasReviews && (
              <section id="reviews" className={styles.reviewsContainer}>
                <div className={clsx(styles.container, styles.reviewsTitle)}>
                  <h2>
                    {entry.treatmentName} {t('treatmentPage.reviews')}
                  </h2>
                </div>
                {entry.reviewsCollection && (
                  <BlockLoader entry={entry.reviewsCollection} />
                )}
                <div className={styles.container}>
                  <Reviews
                    stats={reviewStats}
                    reviews={resultState}
                    title={`${entry.treatmentName} ${t(
                      'treatmentPage.reviews',
                    )}`}
                    noGrid
                  />
                </div>
              </section>
            )}
          </div>
          <div>
            {pageAuthors && (
              <div className={styles.reviewerSideLine}>
                <ReviewerSideLine
                  authorEntry={pageAuthors}
                  lastUpdated={entry.sys.publishedAt}
                />
              </div>
            )}
            {isTableOfContentsShown && (
              <TableOfContents
                titles={titles}
                wrapperClasses={styles.tableOfContents}
              />
            )}
            {sidebar && <div>{sidebar}</div>}
          </div>
        </div>
      </div>
      <div className={styles.getInTouch}>
        <GetInTouch />
      </div>
      <ConversionBar hideContactButton funnelId={funnelId}>
        <div className={styles.conversionBar}>
          <div>
            <p className={styles.conversionBarTitle}>{entry.title}</p>
          </div>
          {entry.startingPrice && (
            <div className={styles.conversionBarPriceContainer}>
              <span className={styles.conversionBarPriceFrom}>{t('from')}</span>
              <span className={styles.conversionBarPriceStartingAt}>
                {t('packagesStartingAt')}
              </span>{' '}
              <span className={styles.conversionBarPrice}>
                {formatCurrency(parseFloat(entry.startingPrice), 'EUR', locale)}
              </span>
            </div>
          )}
        </div>
      </ConversionBar>
    </>
  );
};

export default TreatmentPage;
